import { Injectable } from '@angular/core';
import * as c3 from 'c3';
import * as d3 from 'd3';

@Injectable()
export class GraphsService {
	chartObject: any;
	constructor() {}

	private static toggleLegendStyles(id): void {
		const legendClasses = document.querySelector(`[data-id=${id}]`).classList;
		if (legendClasses.contains('disabled')) {
			legendClasses.remove('disabled');
		} else {
			legendClasses.add('disabled');
		}
	}

	clear(): void {
		if (this.chartObject) {
			this.chartObject = this.chartObject.destroy();
		}
	}

	createLeadOrShootData(data, isLead): void {
		const chart = {
			json: data,
			keys: {
				x: 'label',
				value: ['value']
			},
			names: {
				value: isLead ? 'Leads' : 'Shoots'
			},
			types: {
				value: 'area'
			},
			colors: {
				value: isLead ? '#f27987' : '#22baa1'
			}
		};
		this.drawGraph(chart, false);
	}

	createPaymentsData(payments, paymentsDue, currency): void {
		const chart = {
			json: this.mergeTwoArraysOfObjects(payments, paymentsDue),
			keys: {
				x: 'label',
				value: ['value', 'value1']
			},
			names: {
				value: 'Payments Received',
				value1: 'Unpaid Payments'
			},
			types: {
				value: 'area',
				value1: 'area'
			},
			colors: {
				value: '#7a6fbe',
				value1: '#c2c2c2'
			}
		};
		this.drawGraph(chart, true, currency);
	}

	createRevenueData(lastYear, thisYear, nextYear, currency): void {
		d3.select('.legend').remove();
		const chart = {
			json: this.mergeTwoArraysOfObjects(
				this.mergeTwoArraysOfObjects(lastYear, thisYear),
				nextYear
			),
			keys: {
				x: 'label',
				value: ['value', 'value1', 'value2']
			},
			names: {
				value: 'Last Year',
				value1: 'This Year',
				value2: 'Next Year'
			},
			types: {
				value: 'area',
				value1: 'area',
				value2: 'area'
			},
			colors: {
				value: '#7cc576',
				value1: '#12afcb',
				value2: '#bd8cbf'
			}
		};
		this.drawGraph(chart, true, currency);
	}

	drawGraph(data, needLegend, currency?): void {
		if (this.chartObject) {
			this.chartObject = this.chartObject.destroy();
		}
		const tickValues: Array<number> = this.calculateGridTicksValues(data.json);
		const tickCulling: number = this.calculateTickCulling(
			data.json.length,
			window.innerWidth
		);

		d3.select('.legend').remove();
		const chart = c3.generate({
			bindto: '#chart',
			data,
			transition: {
				duration: 0
			},
			grid: {
				y: {
					show: true
				}
			},
			axis: {
				x: {
					type: 'category',
					tick: {
						width: 100,
						culling: {
							max: tickCulling
						}
					}
				},
				y: {
					max: tickValues[tickValues.length - 1],
					min: 0,
					padding: { top: 5, bottom: 5 },
					tick: {
						format(tickValue: number): string {
							if (tickValue === 0) {
								return '';
							}
							const formattedTickValue = d3.format(',')(tickValue);
							return currency
								? currency + formattedTickValue
								: formattedTickValue;
						},
						values: tickValues
					}
				}
			},
			tooltip: {
				format: {
					title(): string {
						return '';
					}
				},
				contents(values): string {
					const valuesCopy = JSON.parse(JSON.stringify(values));
					let template = '';
					for (let i = 0; i < valuesCopy.length; i++) {
						const formattedValue = d3.format(',')(valuesCopy[i].value);
						switch (valuesCopy[i].name) {
							case 'Payments Received':
								valuesCopy[i].name = 'Received';
								break;
							case 'Unpaid Payments':
								valuesCopy[i].name = 'Unpaid';
								break;
							default:
								break;
						}
						template += `<p><span class='tooltip-name'>${
							valuesCopy[i].name
						}:</span> <span class='tooltip-value'>${
							currency ? currency + formattedValue : formattedValue
						}</span></p>`;
					}
					return `<div class='tooltip-container'>
                              ${template}
                            </div>`;
				}
			},
			legend: {
				show: false
			},
			onresized(): void {
				const windowWidth: number = window.innerWidth;
				const tickCount: number = data.json.length;
				let culling: number;
				// need to think how to call calculateTickCulling to avoid duplicate code
				if (windowWidth > 1200 && windowWidth <= 1390 && tickCount > 26) {
					culling = Math.ceil(tickCount / 2);
				} else if (windowWidth > 600 && windowWidth <= 1200 && tickCount > 16) {
					culling = Math.ceil(tickCount / 2);
				} else if (windowWidth <= 600 && tickCount <= 16) {
					culling = Math.ceil(tickCount / 2);
				} else if (windowWidth <= 600 && tickCount > 16) {
					culling = Math.ceil(tickCount / 4);
				} else {
					culling = 0;
				}
				// quick solution to call "internal" property that doesn't exist in ChartAPI type, need to refactor this in future
				const internal = 'internal';
				chart[internal].config.axis_x_tick_culling_max = culling;
				chart.flush();
			}
		});
		this.chartObject = chart;
		if (needLegend) {
			this.addLegend(chart, data.keys.value);
		}
	}

	private calculateTickCulling = (tickCount, windowWidth): number => {
		if (windowWidth > 1200 && windowWidth <= 1390 && tickCount > 26) {
			return Math.ceil(tickCount / 2);
		} else if (windowWidth > 600 && windowWidth <= 1200 && tickCount > 20) {
			return Math.ceil(tickCount / 2);
		} else if (windowWidth <= 600 && tickCount <= 16) {
			return Math.ceil(tickCount / 2);
		} else if (windowWidth <= 600 && tickCount > 16) {
			return Math.ceil(tickCount / 4);
		}
		return 0;
	};

	private calculateGridTicksValues = (data): Array<number> => {
		const ticks: Array<number> = [];
		let maxValue;
		let maxValue1 = 0;
		let maxValue2 = 0;
		let valueOfTick = 0;

		// find max value in three arrays
		maxValue = this.findMaxValueInArray(data, 'value');
		if (Object.prototype.hasOwnProperty.call(data[0], 'value1')) {
			maxValue1 = this.findMaxValueInArray(data, 'value1');
		}
		if (Object.prototype.hasOwnProperty.call(data[0], 'value2')) {
			maxValue2 = this.findMaxValueInArray(data, 'value2');
		}
		maxValue = Math.ceil(Math.max(maxValue, maxValue1, maxValue2));

		// check not to run an infinite loop
		if (!maxValue || maxValue === 0) {
			return [0, 1, 2, 3, 4];
		}

		// check whether the number is multiple four
		while (maxValue % 4) {
			maxValue++;
		}

		// divide axis Y into 4 equal parts
		while (valueOfTick <= maxValue) {
			ticks.push(valueOfTick);
			valueOfTick += maxValue / 4;
		}
		return ticks;
	};

	private findMaxValueInArray = function(array, valueName): number {
		return Math.max(...array.map((value): number => value[valueName]));
	};

	private mergeTwoArraysOfObjects = function(arrayOne, arrayTwo): Array<{}> {
		const newArray = [];
		arrayOne.forEach((objectOne): void => {
			arrayTwo.forEach((ObjectTwo): void => {
				if (objectOne.label === ObjectTwo.label) {
					if (!objectOne.hasOwnProperty('value1')) {
						newArray.push({ ...objectOne, value1: ObjectTwo.value });
					} else {
						newArray.push({ ...objectOne, value2: ObjectTwo.value });
					}
				}
			});
		});
		return newArray;
	};

	private addLegend = function(chart, value: Array<string>): void {
		d3
			.select('.chart-container')
			.insert('div', '#chart')
			.attr('class', 'legend')
			.selectAll('span')
			.data(value)
			.enter()
			.append('span')
			.attr('data-id', (id): string => id)
			.html((id): string => `<p>${chart.data.names()[id]}</p>`)
			.each(function(id): void {
				d3
					.select(this)
					.insert('div', 'p')
					.attr('class', 'legend-name')
					.style('background-color', chart.color(id));
			})
			.on('mouseover', (id): void => {
				const legendClasses = document.querySelector(`[data-id=${id}]`)
					.classList;
				if (!legendClasses.contains('disabled')) {
					chart.focus(id);
				}
			})
			.on('mouseout', (): void => {
				chart.revert();
			})
			.on('click', (id): void => {
				GraphsService.toggleLegendStyles(id);
				chart.toggle(id);
			});
	};
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=nfw9yna8xro!./web/modern/services/graph/graphs.service.ts